import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";

/**
 * Compact Audio Player for Chat Bubbles
 * Props:
 * - src: string (required) – audio source URL
 * - accentColor: string – primary accent color (default '#3b82f6')
 * - isSystemMessage: boolean – for color adaptation
 */
export default function CustomAudioPlayer({
  src,
  accentColor = "#3b82f6",
  isSystemMessage = false,
}) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  const bars = 18; // Even fewer bars for compact design

  // Create compact waveform bars
  const barHeights = useMemo(() => {
    const seed = (src || "").length || 7;
    let x = seed;
    const heights = [];
    for (let i = 0; i < bars; i += 1) {
      x = (1103515245 * x + 12345) % 2 ** 31;
      const v = (x % 100) / 100;
      const h = 6 + Math.round(v * 14); // Smaller height range: 6-20px
      heights.push(h);
    }
    return heights;
  }, [src]);

  const formatTime = (timeInSec) => {
    if (!isFinite(timeInSec)) return "0:00";
    const minutes = Math.floor(timeInSec / 60);
    const seconds = Math.floor(timeInSec % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    audioRef.current.muted = !audioRef.current.muted;
    setMuted(audioRef.current.muted);
  };

  const onLoaded = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration || 0);
  };

  const onTime = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime || 0);
  };

  useEffect(() => {
    return () => {
      try {
        audioRef.current && audioRef.current.pause();
      } catch (_) {}
    };
  }, []);

  const progress = duration ? Math.min(1, currentTime / duration) : 0;
  const progressBars = Math.round(progress * bars);

  // Colors adapt to system message theme
  const textColor = isSystemMessage ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.7)';
  const waveformColor = isSystemMessage ? 'rgba(255,255,255,0.9)' : accentColor;
  const inactiveWaveformColor = isSystemMessage ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.15)';
  const iconColor = isSystemMessage ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.6)';

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 0.8,
        py: 0.3,
      }}
    >
      {/* Play/Pause Button */}
      <IconButton
        onClick={togglePlay}
        sx={{
          bgcolor: isSystemMessage ? 'rgba(255,255,255,0.2)' : accentColor,
          color: isSystemMessage ? 'white' : 'white',
          width: 28,
          height: 28,
          '&:hover': {
            bgcolor: isSystemMessage ? 'rgba(255,255,255,0.3)' : accentColor,
            transform: 'scale(1.05)',
          },
          transition: 'all 0.2s ease',
          flexShrink: 0,
        }}
      >
        {isPlaying ? (
          <PauseRoundedIcon sx={{ fontSize: 16 }} />
        ) : (
          <PlayArrowRoundedIcon sx={{ fontSize: 16 }} />
        )}
      </IconButton>

      {/* Current Time */}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: textColor,
          minWidth: 32,
          fontSize: '0.7rem',
          flexShrink: 0,
        }}
      >
        {formatTime(currentTime)}
      </Typography>

      {/* Compact Waveform */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '1.5px',
          flex: 1,
          height: 24,
          px: 0.3,
        }}
      >
        {barHeights.map((h, i) => (
          <Box
            key={i}
            sx={{
              width: '2.5px',
              height: `${h}px`,
              borderRadius: '1.25px',
              background: i < progressBars ? waveformColor : inactiveWaveformColor,
              transition: 'all 0.3s ease',
              opacity: i < progressBars ? 1 : 0.6,
            }}
          />
        ))}
      </Box>

      {/* Volume Toggle */}
      <IconButton
        onClick={toggleMute}
        sx={{
          color: iconColor,
          flexShrink: 0,
          width: 26,
          height: 26,
          '&:hover': {
            color: isSystemMessage ? 'white' : accentColor,
            bgcolor: isSystemMessage ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
          },
        }}
      >
        {muted ? (
          <VolumeOffRoundedIcon sx={{ fontSize: 16 }} />
        ) : (
          <VolumeUpRoundedIcon sx={{ fontSize: 16 }} />
        )}
      </IconButton>

      {/* Duration */}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 500,
          color: textColor,
          minWidth: 32,
          fontSize: '0.7rem',
          flexShrink: 0,
        }}
      >
        {formatTime(duration)}
      </Typography>

      <audio
        ref={audioRef}
        src={src}
        onLoadedMetadata={onLoaded}
        onTimeUpdate={onTime}
        onEnded={() => setIsPlaying(false)}
        style={{ display: 'none' }}
      />
    </Box>
  );
}